import { useEffect } from "react";
import {
  Activity,
  ArrowUpRight,
  BookOpenCheck,
  Brain,
  CalendarDays,
  Check,
  ClipboardCheck,
  Clock3,
  Compass,
  Globe2,
  Sparkles,
  Target,
  VideoOff,
} from "lucide-react";
import "@/App.css";

const CHECKOUT_URL = "https://learn.sultanmahmud.in/web/checkout/6aac223be4b807d75981d028";
const START_DATE = "13 Oct 2026";
const PORTRAIT = "https://tagmango.com/assets/coach-754ab8e06c8e4b799bab2cd00ff6bb0e.png";
const AWARD = "https://tagmango.com/assets/diamond500by500-29e5676b4fa5ec12872a9c445d766fb5.png";

const outcomes = [
  ["Clarity", "See where you are right now.", Compass, "blue"],
  ["Focus", "Identify what deserves your attention.", Target, "orange"],
  ["Direction", "Decide what meaningful change you want.", Sparkles, "teal"],
  ["Awareness", "Recognise patterns that may be getting in your way.", Brain, "purple"],
  ["Action", "Choose one action you can actually practise.", Activity, "green"],
  ["Reset", "Create your first practical 90-day direction.", ClipboardCheck, "indigo"],
];

const challengeProcess = [
  ["Learn", "Understand one practical concept."],
  ["Reflect", "Look honestly at your current situation."],
  ["Act", "Complete a guided exercise."],
  ["Track", "Notice what you are actually doing."],
  ["Reset", "Decide your next practical step."],
];

const days = [
  ["01", "Aware — Life Clarity", "Where am I?", "Awareness Map", Compass],
  ["02", "Align — Vision & Direction", "Where do I want to go?", "90-Day Direction Statement", Target],
  ["03", "Patterns & Resistance", "What may interfere?", "Pattern & Resistance Map", Brain],
  ["04", "Act → Build — Action & Growth", "What will I practise?", "Personal Action System", Activity],
  ["05", "Adapt → Integrate — 90-Day Reset", "How will I continue, adjust and sustain?", "90-Day Reset Plan", ClipboardCheck],
];

const included = [
  ["5 Live Transformation Sessions", "Participate, reflect and make decisions live.", CalendarDays],
  ["5-Day Guided Reset Journey", "Move through the approved reset curriculum step by step.", Compass],
  ["Practical Daily Exercises", "Turn each session into something you can practise.", Check],
  ["Reflection & Action Worksheets", "Write, notice patterns and choose your next action.", BookOpenCheck],
  ["90-Day Reset Plan", "Create a realistic direction for what comes next.", Target],
  ["Live Participation & Community Support", "Show up, do the work and stay connected.", Globe2],
];

const familiarStatements = [
  "I know I need to change something, but I don't know where to start.",
  "I keep postponing things that matter to me.",
  "I start with motivation but struggle to stay consistent.",
  "My mind feels cluttered and I struggle to focus.",
  "I have goals, but I don't have a clear direction.",
  "I keep repeating patterns I know aren't helping me.",
  "I want to improve my life but feel overwhelmed by where to begin.",
];

const joinSteps = [
  ["01", "Register for ₹55", "Complete your challenge registration."],
  ["02", "Get the joining details", "Receive everything you need to join live."],
  ["03", "Join live at 6:30 AM", "Be ready when the live session begins."],
  ["04", "Bring a notebook", "Participate, write and reflect."],
  ["05", "Complete the exercise", "Take one practical step each day."],
  ["06", "Build your 90-day plan", "Finish with your personal reset direction."],
];

const faqs = [
  ["Is this live or recorded?", "It is live. There are no recordings."],
  ["When does the next challenge start?", "The next batch starts on 13 October 2026."],
  ["What time is the session?", "6:30–8:00 AM IST."],
  ["What language will you use?", "Simple English with natural Assamese/English explanations."],
  ["Do I need prior knowledge?", "No."],
  ["What should I bring?", "A notebook, pen and willingness to participate."],
  ["Is this therapy or counselling?", "No. It is a practical personal-growth and life-reset experience."],
  ["Will I get a complete 90-day transformation in five days?", "No. The five days are designed to create clarity, direction and a practical starting point. On Day 5, participants will learn about the optional next-stage 90-Day Transformation Program."],
];

function CTA({ children, testId, inverse = false }) {
  return (
    <a
      className={`cta ${inverse ? "cta--inverse" : ""}`}
      href={CHECKOUT_URL}
      data-testid={testId}
      aria-label={`${children} — continue to challenge checkout`}
    >
      <span>{children}</span><ArrowUpRight aria-hidden="true" size={19} strokeWidth={2.5} />
    </a>
  );
}

function Reveal({ children, className = "", testId }) {
  return <div className={`reveal ${className}`} data-testid={testId}>{children}</div>;
}

function Hero() {
  return (
    <header className="hero" data-testid="hero-section">
      <img className="hero__portrait" src={PORTRAIT} alt="Sultan Mahmud, trainer for the 5-Day Life Reset Challenge" data-testid="hero-portrait" />
      <div className="hero__veil" aria-hidden="true" />
      <nav className="nav shell" aria-label="Primary" data-testid="primary-navigation">
        <a href="#top" className="brand" data-testid="brand-home-link"><span>SKILLIFY</span> LIFE</a>
        <a href={CHECKOUT_URL} className="nav__link" data-testid="nav-join-link">Join for ₹55 <ArrowUpRight size={15} aria-hidden="true" /></a>
      </nav>
      <div className="hero__content shell" id="top">
        <div className="hero__copy reveal is-visible">
          <p className="eyebrow" data-testid="hero-eyebrow"><Sparkles size={14} aria-hidden="true" /> 5-DAY LIFE RESET CHALLENGE</p>
          <h1 data-testid="hero-heading">Feeling Stuck in Life?<br /><span>Take 5 Days to Reset.</span></h1>
          <p className="hero__sub" data-testid="hero-subheading">A practical live experience to help you slow down, get clear, choose your direction, and take your first meaningful step.</p>
          <div className="hero__badges" data-testid="hero-event-details">
            <span data-testid="hero-start-date-badge"><CalendarDays size={15} aria-hidden="true" /> STARTS {START_DATE.toUpperCase()}</span>
            <span data-testid="hero-duration-badge"><CalendarDays size={15} aria-hidden="true" /> 5 DAYS</span>
            <span data-testid="hero-time-badge"><Clock3 size={15} aria-hidden="true" /> 6:30–8:00 AM IST</span>
            <span data-testid="hero-live-badge">LIVE ONLINE</span>
            <span data-testid="hero-language-badge"><Globe2 size={15} aria-hidden="true" /> BASIC ENGLISH + ASSAMESE</span>
          </div>
          <div className="hero__offer" data-testid="hero-offer">
            <div>
              <small data-testid="hero-offer-label">SPECIAL CHALLENGE PRICE</small>
              <div className="price-row" data-testid="hero-price-row"><strong>₹55</strong><span>one-time registration</span></div>
            </div>
            <div className="live-only-mini" data-testid="hero-live-only"><VideoOff size={20} aria-hidden="true" /><strong>Live only<br />No recordings</strong></div>
          </div>
          <CTA testId="hero-join-button">JOIN THE 5-DAY LIFE RESET</CTA>
        </div>
        <div className="hero__signature" data-testid="hero-trainer-caption">
          <strong>Sultan Mahmud</strong><span>Founder, Skillify Life</span>
        </div>
      </div>
    </header>
  );
}

function Problem() {
  return (
    <section className="section problem" aria-labelledby="problem-title" data-testid="problem-section">
      <Reveal className="narrow" testId="problem-content">
        <p className="section-index" data-testid="problem-index">01 — THE STARTING POINT</p>
        <h2 id="problem-title" data-testid="problem-heading">You Know You Need to Change.<br />But Where Do You Start?</h2>
        <div className="problem__copy" data-testid="problem-copy">
          <p>You know something needs to change, but you don't know where to start. You keep thinking about change but struggle to take action.</p>
          <p>You may feel stuck in the same patterns, have goals without clear direction, or start things but struggle to continue. You want meaningful change—not another motivational lecture.</p>
        </div>
        <blockquote data-testid="problem-core-message">The problem isn't always a lack of information.<br /><span>It's a lack of clarity, structure and consistent action.</span></blockquote>
        <strong className="blue-line" data-testid="problem-closing">That’s what these five days are designed to help you work on.</strong>
      </Reveal>
    </section>
  );
}

function Outcomes() {
  return (
    <section className="section outcomes" aria-labelledby="outcomes-title" data-testid="outcomes-section">
      <div className="shell">
        <Reveal className="section-head" testId="outcomes-header">
          <p className="section-index" data-testid="outcomes-index">02 — REALISTIC OUTCOMES</p>
          <h2 id="outcomes-title" data-testid="outcomes-heading">What Could Change When You Give Yourself 5 Days?</h2>
          <p data-testid="outcomes-intro">Not a miracle. A focused pause to see clearly and choose practical next steps.</p>
        </Reveal>
        <div className="outcomes__grid" data-testid="outcomes-grid">
          {outcomes.map(([title, copy, Icon, tone], index) => (
            <article className="outcome-card reveal" key={title} data-testid={`outcome-card-${index + 1}`}>
              <span className={`outcome-card__icon outcome-card__icon--${tone}`} data-testid={`outcome-icon-${index + 1}`}><Icon size={19} aria-hidden="true" /></span>
              <h3 data-testid={`outcome-title-${index + 1}`}>{title}</h3>
              <p data-testid={`outcome-copy-${index + 1}`}>{copy}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Method() {
  return (
    <section className="section method" aria-labelledby="method-title" data-testid="method-section">
      <div className="shell method__grid">
        <Reveal testId="method-copy">
          <p className="section-index section-index--dark" data-testid="method-index">03 — IMPLEMENTATION, NOT HYPE</p>
          <h2 id="method-title" data-testid="method-heading">This Isn't 5 Days of Motivation.<br />You'll Actually Do the Work.</h2>
          <p data-testid="method-description">You'll write. Reflect. Participate. Make decisions. Try something. And build your own reset plan.</p>
          <strong className="method__highlight" data-testid="method-highlight">This is not about feeling motivated for five days. It's about leaving with something you can actually use.</strong>
        </Reveal>
        <Reveal className="method__sequence" testId="method-sequence">
          {challengeProcess.map(([step, copy], index) => (
            <div className="method__step" key={step} data-testid={`method-step-${step.toLowerCase()}`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div><strong>{step}</strong><p>{copy}</p></div>
              {index < challengeProcess.length - 1 && <b aria-hidden="true">↓</b>}
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section className="section journey" aria-labelledby="journey-title" data-testid="journey-section">
      <div className="shell">
        <Reveal className="section-head" testId="journey-header">
          <p className="section-index" data-testid="journey-index">04 — FIVE DAYS, ONE DIRECTION</p>
          <h2 id="journey-title" data-testid="journey-heading">Your 5-Day Reset Journey</h2>
        </Reveal>
        <div className="journey__grid" data-testid="journey-grid">
          {days.map(([number, title, question, output, Icon], index) => (
            <article className={`day-card reveal day-card--${index + 1}`} key={number} data-testid={`day-card-${index + 1}`}>
              <div className="day-card__top"><span data-testid={`day-number-${index + 1}`}>DAY {number}</span><Icon size={22} aria-hidden="true" /></div>
              <h3 data-testid={`day-title-${index + 1}`}>{title}</h3>
              <p data-testid={`day-copy-${index + 1}`}>{question}</p>
              <div className="day-card__output" data-testid={`day-output-${index + 1}`}><span>OUTPUT</span><strong>{output}</strong></div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Includes() {
  return (
    <section className="section includes" aria-labelledby="includes-title" data-testid="includes-section">
      <div className="shell">
        <Reveal className="section-head" testId="includes-header">
          <p className="section-index" data-testid="includes-index">05 — WHAT YOU RECEIVE</p>
          <h2 id="includes-title" data-testid="includes-heading">Everything You Get for ₹55</h2>
        </Reveal>
        <div className="includes__grid" data-testid="includes-grid">
          {included.map(([title, copy, Icon], index) => (
            <article className="include-card reveal" key={title} data-testid={`include-card-${index + 1}`}>
              <Icon size={24} aria-hidden="true" />
              <h3 data-testid={`include-title-${index + 1}`}>{title}</h3>
              <p data-testid={`include-copy-${index + 1}`}>{copy}</p>
            </article>
          ))}
        </div>
        <Reveal className="live-notice" testId="live-only-notice">
          <VideoOff size={30} aria-hidden="true" />
          <div><strong data-testid="live-only-heading">NO RECORDINGS</strong><p data-testid="live-only-copy">The value comes from showing up and doing the work live.</p></div>
        </Reveal>
        <Reveal className="includes__cta" testId="includes-cta-wrap">
          <CTA testId="includes-join-button">JOIN THE 5-DAY LIFE RESET</CTA>
        </Reveal>
      </div>
    </section>
  );
}

function Trainer() {
  return (
    <section className="section credibility" aria-labelledby="trainer-title" data-testid="trainer-section">
      <div className="shell credibility__grid">
        <Reveal className="credibility__collage" testId="trainer-image-wrap">
          <img className="credibility__award" src={AWARD} alt="Sultan Mahmud receiving the Diamond Star award with his mentor" data-testid="trainer-award-image" />
          <p className="image-note" data-testid="trainer-image-note">Real experience. Real learning. Continued growth.</p>
        </Reveal>
        <Reveal className="credibility__copy" testId="trainer-content">
          <p className="section-index" data-testid="trainer-index">06 — PRACTICAL GUIDANCE</p>
          <h2 id="trainer-title" data-testid="trainer-heading">Meet Your Trainer</h2>
          <h3 data-testid="trainer-name">Sultan Mahmud</h3>
          <p className="role" data-testid="trainer-role">Founder, Skillify Life</p>
          <p className="trainer-proof" data-testid="trainer-proof">14+ years of teaching and training experience • 10,000+ learners trained</p>
          <p data-testid="trainer-biography">Sultan Mahmud is the founder of Skillify Life and has spent more than 14 years teaching and training learners. His work combines personal growth, practical learning and modern technology to help people move from knowing what to do to actually doing it.</p>
          <blockquote data-testid="trainer-positioning">“Don't just listen. Do the exercise.”</blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function Language() {
  return (
    <section className="language" aria-labelledby="language-title" data-testid="language-section">
      <div className="shell language__inner">
        <Reveal className="language__icon" testId="language-icon"><Globe2 size={34} aria-hidden="true" /></Reveal>
        <Reveal className="language__copy" testId="language-content">
          <p className="section-index section-index--dark" data-testid="language-index">SIMPLE & COMFORTABLE</p>
          <h2 id="language-title" data-testid="language-heading">Learn in a Simple, Comfortable Language</h2>
          <p data-testid="language-copy">No complicated jargon. No heavy theory. Just <strong>simple English + natural Assamese</strong>, with practical examples you can relate to.</p>
        </Reveal>
      </div>
    </section>
  );
}

function Familiar() {
  return (
    <section className="section familiar" aria-labelledby="familiar-title" data-testid="familiar-section">
      <div className="shell">
        <Reveal className="section-head" testId="familiar-header">
          <p className="section-index" data-testid="familiar-index">07 — IS THIS FOR YOU?</p>
          <h2 id="familiar-title" data-testid="familiar-heading">Does Any of This Sound Familiar?</h2>
        </Reveal>
        <div className="familiar__grid" data-testid="familiar-grid">
          {familiarStatements.map((statement, index) => (
            <article className="familiar-card reveal" key={statement} data-testid={`familiar-card-${index + 1}`}>
              <span aria-hidden="true">“</span><p data-testid={`familiar-copy-${index + 1}`}>{statement}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function AfterJoin() {
  return (
    <section className="section after-join" aria-labelledby="after-join-title" data-testid="after-join-section">
      <div className="shell">
        <Reveal className="section-head" testId="after-join-header">
          <p className="section-index" data-testid="after-join-index">08 — A SIMPLE PROCESS</p>
          <h2 id="after-join-title" data-testid="after-join-heading">What Happens After You Join?</h2>
        </Reveal>
        <div className="after-join__steps" data-testid="after-join-steps">
          {joinSteps.map(([number, title, copy], index) => (
            <article className="join-step reveal" key={number} data-testid={`join-step-${index + 1}`}>
              <span data-testid={`join-step-number-${index + 1}`}>{number}</span>
              <h3 data-testid={`join-step-title-${index + 1}`}>{title}</h3>
              <p data-testid={`join-step-copy-${index + 1}`}>{copy}</p>
            </article>
          ))}
        </div>
        <Reveal className="after-join__notice" testId="after-join-notice">There are no recordings. The value comes from showing up and doing the work live.</Reveal>
      </div>
    </section>
  );
}

function Offer() {
  return (
    <section className="section offer" id="registration" aria-labelledby="offer-title" data-testid="offer-section">
      <div className="offer__halo" aria-hidden="true" />
      <div className="shell offer__layout">
        <Reveal className="offer__headline" testId="offer-header">
          <p className="section-index section-index--dark" data-testid="offer-index">SPECIAL CHALLENGE PRICE</p>
          <h2 id="offer-title" data-testid="offer-heading">Join the 5-Day Life Reset Challenge</h2>
          <p data-testid="offer-copy">A practical live experience designed to help you move from awareness to action for ₹55.</p>
        </Reveal>
        <Reveal className="offer-card" testId="offer-card">
          <p className="offer-card__label" data-testid="offer-card-label">5-DAY LIFE RESET CHALLENGE</p>
          <div className="offer-card__price" data-testid="offer-card-price"><strong>₹55</strong><span>one-time registration</span></div>
          <div className="offer-card__meta" data-testid="offer-card-details"><span>STARTS {START_DATE.toUpperCase()}</span><span>5 DAYS</span><span>6:30–8:00 AM IST</span><span>LIVE ONLINE</span></div>
          <div className="offer-card__live" data-testid="offer-live-only"><VideoOff size={20} aria-hidden="true" /><span>Live participation only • No recordings</span></div>
          <CTA testId="offer-join-button" inverse>JOIN THE 5-DAY LIFE RESET</CTA>
        </Reveal>
      </div>
    </section>
  );
}

function NextStepTeaser() {
  return (
    <section className="next-step" aria-labelledby="next-step-title" data-testid="next-step-section">
      <Reveal className="next-step__content" testId="next-step-content">
        <p className="section-index" data-testid="next-step-index">AFTER THE CHALLENGE</p>
        <h2 id="next-step-title" data-testid="next-step-heading">The 5 Days Are Just the Beginning.</h2>
        <p data-testid="next-step-copy-one">During these five days, you'll create clarity, direction and a practical starting point. But real change takes longer than five mornings.</p>
        <p data-testid="next-step-copy-two">If you want structured support to continue the work, you'll have the opportunity on Day 5 to learn about the next stage of Skillify Life.</p>
      </Reveal>
    </section>
  );
}

function FAQ() {
  return (
    <section className="section faq" aria-labelledby="faq-title" data-testid="faq-section">
      <div className="shell faq__layout">
        <Reveal className="faq__intro" testId="faq-header">
          <p className="section-index" data-testid="faq-index">09 — BEFORE YOU JOIN</p>
          <h2 id="faq-title" data-testid="faq-heading">Questions, Answered Clearly.</h2>
          <p data-testid="faq-intro">Everything important about the live challenge, before you register.</p>
        </Reveal>
        <Reveal className="faq__list" testId="faq-list">
          {faqs.map(([question, answer], index) => (
            <details key={question} data-testid={`faq-item-${index + 1}`}>
              <summary data-testid={`faq-question-${index + 1}`}>{question}<span aria-hidden="true">+</span></summary>
              <p data-testid={`faq-answer-${index + 1}`}>{answer}</p>
            </details>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section final" aria-labelledby="final-title" data-testid="final-cta-section">
      <Reveal className="final__content" testId="final-cta-content">
        <h2 id="final-title" data-testid="final-heading">5 Days.<br />One Reset.<br /><span>A Clearer Direction.</span></h2>
        <p data-testid="final-subheading">You don't need to fix your whole life in five days.<br />You just need to see clearly, choose what matters, and take the first step.</p>
        <div className="final__price" data-testid="final-price"><span>SPECIAL CHALLENGE PRICE</span><strong>₹55</strong></div>
        <CTA testId="final-join-button">JOIN THE 5-DAY LIFE RESET</CTA>
        <p className="final__details" data-testid="final-event-details">Starts {START_DATE} • 5 Days • Live Online • 6:30–8:00 AM IST • Basic English + Assamese</p>
        <p className="final__recording-note" data-testid="final-recording-note">Live participation only. No recordings will be provided.</p>
      </Reveal>
    </section>
  );
}

function App() {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible"));
    }, { threshold: 0.1 });
    document.querySelectorAll(".reveal").forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="site" data-testid="skillify-life-reset-page">
      <Hero />
      <main data-testid="main-content">
        <Problem /><Outcomes /><Method /><Journey /><Includes /><Trainer /><Language />
        <Familiar /><AfterJoin /><Offer /><NextStepTeaser /><FAQ /><FinalCTA />
      </main>
      <footer className="footer" data-testid="site-footer"><strong data-testid="footer-brand">SKILLIFY LIFE</strong><span data-testid="footer-description">5-Day Life Reset Challenge • Sultan Mahmud</span></footer>
      <a className="mobile-sticky" href={CHECKOUT_URL} data-testid="mobile-sticky-join-button">JOIN FOR ₹55 <ArrowUpRight size={18} aria-hidden="true" /></a>
    </div>
  );
}

export default App;